/**
 * URL utility functions for handling account numbers in URLs
 */

/**
 * Utility function to deduplicate and clean accounts
 * @param {Array} accountsList - The list of accounts to clean up
 * @param {string} userEmail - Optional user email for fallback name generation
 * @returns {Array} - Cleaned and deduplicated accounts
 */
export const cleanupAccounts = (accountsList, userEmail = null) => {
  if (!Array.isArray(accountsList) || accountsList.length === 0) {
    return [];
  }
  
  // Deduplicate based on acctId
  const deduplicatedAccounts = Array.from(
    new Map(accountsList.map(acc => [acc.acctId, acc])).values()
  );
  
  // Clean up names for each account
  return deduplicatedAccounts.map(acc => {
    // Ensure all accounts have valid names
    if (acc.accountName && acc.accountName !== 'undefined' && acc.accountName !== 'null') {
      acc.acctName = acc.accountName;
      acc.name = acc.accountName;
    } else if (acc.acctName && acc.acctName !== 'undefined' && acc.acctName !== 'null') {
      acc.accountName = acc.acctName;
      acc.name = acc.acctName;
    } else if (acc.name && acc.name !== 'undefined' && acc.name !== 'null') {
      acc.accountName = acc.name;
      acc.acctName = acc.name;
    } else {
      const fallbackName = userEmail ? userEmail.split('@')[0] : 'Account';
      acc.accountName = fallbackName;
      acc.acctName = fallbackName;
      acc.name = fallbackName;
    }
    
    return acc;
  });
};

/**
 * Appends the account number to a URL path
 * @param {string} path - The URL path to append the account number to
 * @param {string} accountNo - Optional account number to use instead of localStorage
 * @returns {string} - The URL with the account number appended as a query parameter
 */
export const appendAccountToUrl = (path, accountNo = null) => {
  // Use provided account number or fall back to localStorage
  const targetAccountNo = accountNo 
  
  if (targetAccountNo) {
    // First check if URL already has this account number
    const url = new URL(path, window.location.origin);
    const urlAccountNo = url.searchParams.get('acc');
    
    // If URL already has the correct account number, return as is
    if (urlAccountNo === targetAccountNo) {
      return path;
    }
    
    // Otherwise, update or add the account parameter
    return path.includes('?') ? `${path}&acc=${targetAccountNo}` : `${path}?acc=${targetAccountNo}`;
  }
  
  return path;
};

/**
 * Gets the account number from the URL search params
 * @param {string} search - The URL search string (usually from location.search)
 * @returns {string|null} - The account number or null if not found
 */
export const getAccountFromUrl = (search) => {
  // If no search parameter provided, use current window location
  const searchParams = search || window.location.search;
  const params = new URLSearchParams(searchParams);
  return params.get('acc');
};

/**
 * Updates the current URL with a new account number without page reload
 * @param {string} accountNo - The new account number to update in URL
 * @param {boolean} isRedirect - Whether to redirect to the new URL (default: false)
 * @returns {string} - The updated URL string
 */
export const updateUrlWithAccount = (accountNo, isRedirect = false) => {
  if (!accountNo) return window.location.href;
  
  const url = new URL(window.location.href);
  const params = url.searchParams;
  
  // Update or add the acc parameter
  params.set('acc', accountNo);
  
  const newUrl = url.toString();
  
  if (isRedirect) {
    // Redirect to the new URL
    window.location.href = newUrl;
  } else {
    // Update the URL without reloading the page
    window.history.replaceState({}, '', newUrl);
  }
  
  return newUrl;
};

/**
 * Ensures that the current page URL includes the account number
 * This should be called in the useEffect of components that need the account number in URL
 */
export const ensureAccountInUrl = () => {
  const accountNo = localStorage.getItem('acctNo');
  if (!accountNo) {
    // Try to get account number from acctId if available
    const accounts = localStorage.getItem('accounts');
    const acctId = localStorage.getItem('acctId');
    
    if (accounts && acctId) {
      try {
        const accountsData = JSON.parse(accounts);
        const currentAccount = accountsData.find(acc => acc.acctId === acctId);
        if (currentAccount && currentAccount.acctNo) {
          localStorage.setItem('acctNo', currentAccount.acctNo);
          updateUrlWithAccount(currentAccount.acctNo, false);
          return;
        }
      } catch (error) {
        console.error('Error parsing accounts data:', error);
      }
    }
    return;
  }
  
  const url = new URL(window.location.href);
  const urlAccountNo = url.searchParams.get('acc');
  
  // Only update URL if account number is different
  if (urlAccountNo !== accountNo) {
    updateUrlWithAccount(accountNo, false);
  }
};

/**
 * Prepares navigation link with account number
 * Use this for Link components and programmatic navigation
 * @param {string} to - The destination path
 * @param {string} accountNo - Optional account number to use instead of localStorage
 * @returns {string} - Path with account number added
 */
export const prepareNavigation = (to, accountNo = null) => {
  // Handle both string paths and object paths (with pathname, search, etc.)
  if (typeof to === 'object' && to !== null) {
    const { pathname, search, hash } = to;
    const searchParams = new URLSearchParams(search || '');
    
    const targetAccountNo = accountNo || localStorage.getItem('acctNo');
    if (targetAccountNo) {
      searchParams.set('acc', targetAccountNo);
    }
    
    return {
      ...to,
      pathname,
      search: searchParams.toString() ? `?${searchParams.toString()}` : '',
      hash: hash || ''
    };
  }
  
  // Handle string paths
  return appendAccountToUrl(to, accountNo);
};
